/**
 * useChartWidth — the measured pixel width of a chart's own box.
 *
 * The kit's SVG charts drew into a fixed 560-wide viewBox with
 * `preserveAspectRatio="xMidYMid meet"` and a fixed pixel height. That combination
 * does NOT stretch: `meet` scales by the smaller ratio, and with the height pinned
 * the scale is 1, so a 560px drawing sat marooned in the middle of a 1200px panel
 * with the rest as dead air. The other way round — `height: auto` — is worse: the
 * plot scales uniformly, so a wider panel yields a TALLER chart with bigger type.
 * Measuring the box and drawing at 1:1 into a viewBox of that width is what makes a
 * chart grow in the one dimension that shows more data.
 *
 * A CALLBACK ref, not a ref object: a chart that returns an empty state before its
 * plot (MarginBreakdownChart does) mounts the measured div only later, and a mount
 * effect would have already run against a null ref and never re-attached — the chart
 * would silently sit at its fallback width forever.
 *
 * Falls back to `fallback` before layout and under jsdom (no ResizeObserver, zero
 * clientWidth), so charts still render deterministically in tests.
 */

import { useCallback, useRef, useState } from 'react'

export function useChartWidth(fallback = 560): [(el: HTMLDivElement | null) => void, number] {
  const [width, setWidth] = useState(fallback)
  const observer = useRef<ResizeObserver | null>(null)

  const attach = useCallback(
    (el: HTMLDivElement | null): void => {
      observer.current?.disconnect()
      observer.current = null
      if (!el || typeof ResizeObserver === 'undefined') return
      const apply = (w: number): void => setWidth(w > 0 ? Math.round(w) : fallback)
      apply(el.clientWidth)
      observer.current = new ResizeObserver((entries) => apply(entries[0].contentRect.width))
      observer.current.observe(el)
    },
    [fallback]
  )

  // Teardown belongs to the ref callback, NOT a mount effect. React calls a callback
  // ref with null on unmount, which disconnects above. A `useEffect(…, [])` doing the
  // disconnect looks equivalent and is not: StrictMode's simulated unmount fires that
  // cleanup, and since the effect body only RETURNED a cleanup there was nothing to
  // re-attach — the observer stayed dead for the life of the page, so every chart
  // froze at its first measured width and letterboxed on the next resize.
  return [attach, width]
}
